import React, { useEffect } from "react";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const BulkActionsPage = () => {
  useEffect(() => {
    document.title = "ChatGPT Power-Up: Bulk Actions - Delete Multiple Chats";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Perform bulk actions on your ChatGPT conversations. Delete multiple chats at once and manage your conversations efficiently.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-4xl mb-4">🗑️</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Bulk Actions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Manage multiple ChatGPT conversations at once. Delete, organize, and maintain your chat history with powerful bulk operations.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Multi-Select</h3>
            <p className="text-gray-600">
              Select multiple conversations at once with our intuitive multi-select interface. Choose exactly what you want to manage.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">🗑️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Bulk Delete</h3>
            <p className="text-gray-600">
              Delete multiple conversations in one action. Save time and keep your chat history clean and organized.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">📁</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Bulk Organize</h3>
            <p className="text-gray-600">
              Move multiple conversations to folders at once. Organize your chats efficiently without the manual work.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">🔄</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Batch Operations</h3>
            <p className="text-gray-600">
              Perform various batch operations on your conversations. From archiving to starring, manage everything efficiently.
            </p>
          </motion.div>
        </div>

        {/* Screenshot Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            See Bulk Actions in Action
          </h2>
          <div className="max-w-4xl mx-auto">
            <img 
              src="/bulk-actions.png" 
              alt="Bulk actions feature screenshot" 
              className="rounded-lg shadow-lg w-full"
            />
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center bg-white p-8 rounded-xl shadow-md"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Master Your ChatGPT Conversations?
          </h2>
          <p className="text-gray-600 mb-6">
            Join thousands of users who have transformed their ChatGPT experience with powerful bulk actions.
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
          >
            <img src="/chrome.png" alt="Chrome" className="w-5 h-5" />
            Install Now - Free
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default BulkActionsPage; 